import { useState, useRef, useEffect } from 'react';
import Modal from './Modal';
import { useTranslation } from '../i18n';
import { formatRelativeDate } from '../utils/dateUtils';
import './ProjectForm.scss';

/** Preset color options for projects */
const DEFAULT_COLORS = [
  '#4285f4', '#ea4335', '#fbbc04', '#34a853', 
  '#ff6d01', '#46bdc6', '#7baaf7', '#f07b72',
  '#9334e6', '#ff4081', '#00bcd4', '#4caf50'
];

/** Special value for deleting events/milestones */
const DELETE_ITEMS_VALUE = '__DELETE__';

/**
 * Modal form for creating or editing a project.
 * Displays name input and color picker grid.
 * 
 * @param {Object} props
 * @param {Object} [props.project] - Existing project to edit (null for create)
 * @param {Array} [props.otherProjects] - List of other projects for merge dropdown
 * @param {Function} props.onConfirm - Handler for save (name, color) => void
 * @param {Function} props.onCancel - Handler for cancel/close
 * @param {Function} [props.onDelete] - Handler for delete (targetProjectId) => void
 * @param {boolean} [props.canDelete] - Whether delete button should be enabled
 */
function ProjectForm({ project, otherProjects = [], onConfirm, onCancel, onDelete, canDelete = true }) {
  const { t } = useTranslation();
  // Get display name for initial value (translates system project names)
  const getInitialName = () => {
    if (!project) return '';
    return project.isSystem ? t('holiday.holidaysName') : project.name;
  };
  const [name, setName] = useState(getInitialName);
  const [color, setColor] = useState(project?.color || DEFAULT_COLORS[0]);
  const [showDeleteDropdown, setShowDeleteDropdown] = useState(false);
  const [targetProject, setTargetProject] = useState(otherProjects[0]?.id || DELETE_ITEMS_VALUE);
  const inputRef = useRef(null);

  const isEditing = !!project;

  // Focus input on mount
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (name.trim()) {
      onConfirm(name.trim(), color);
    }
  };

  return (
    <Modal 
      isOpen 
      onClose={onCancel} 
      title={isEditing ? t('project.edit') : t('project.new')}
    >
      <form onSubmit={handleSubmit} className="project-form__form">
        <div className="project-form__field">
          <label className="project-form__label">{t('project.name')}</label>
          <input
            ref={inputRef}
            type="text"
            className="project-form__input"
            placeholder={t('project.placeholder')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={50}
          />
        </div>

        <div className="project-form__field">
          <label className="project-form__label">{t('project.color')}</label>
          <div className="project-form__colors">
            {DEFAULT_COLORS.map(colorOption => (
              <button
                key={colorOption}
                type="button"
                className={`project-form__color ${color === colorOption ? 'project-form__color--active' : ''}`}
                style={{ backgroundColor: colorOption }}
                onClick={() => setColor(colorOption)}
                title={colorOption}
              />
            ))}
          </div>
        </div>

        {/* Share info for included projects */}
        {project?.isIncluded && (
          <div className="project-form__share-info">
            <div className="project-form__share-icon">↗</div>
            <div className="project-form__share-details">
              <span className="project-form__share-label">{t('sharing.sharedProject')}</span>
              <span className="project-form__share-by">
                {t('sharing.sharedByOn', { 
                  name: project.sharedBy, 
                  date: formatRelativeDate(project.sharedAt)
                })}
              </span>
              {project.originalName !== project.name && (
                <span className="project-form__share-original">
                  {t('sharing.originalName', { name: project.originalName })}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Delete section with dropdown - not shown for included projects */}
        {isEditing && onDelete && showDeleteDropdown && !project?.isIncluded && (
          <div className="project-form__delete-section">
            <div className="project-form__field">
              <label className="project-form__label">{t('project.moveItemsTo')}</label>
              <select
                className="project-form__select"
                value={targetProject}
                onChange={(e) => setTargetProject(e.target.value)}
              >
                {otherProjects.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
                <option value={DELETE_ITEMS_VALUE}>{t('project.deleteItems')}</option>
              </select>
            </div>
            <div className="project-form__delete-actions">
              <button 
                type="button" 
                className="project-form__btn project-form__btn--cancel" 
                onClick={() => setShowDeleteDropdown(false)}
              >
                {t('common.cancel')}
              </button>
              <button 
                type="button" 
                className="project-form__btn project-form__btn--delete" 
                onClick={() => onDelete(targetProject === DELETE_ITEMS_VALUE ? null : targetProject)}
              >
                {t('project.confirmDelete')}
              </button>
            </div>
          </div>
        )}

        <div className="project-form__actions">
          {isEditing && onDelete && !showDeleteDropdown && !project?.isIncluded && (
            <div 
              className="project-form__delete-wrapper"
              title={!canDelete ? t('project.cannotDeleteLast') : ''}
            >
              <button 
                type="button" 
                className="project-form__btn project-form__btn--delete" 
                onClick={() => setShowDeleteDropdown(true)}
                disabled={!canDelete}
              >
                {t('common.delete')}
              </button>
            </div>
          )}
          {!showDeleteDropdown && (
            <div className="project-form__actions-right">
              <button 
                type="button" 
                className="project-form__btn project-form__btn--cancel" 
                onClick={onCancel}
              >
                {t('common.cancel')}
              </button>
              <button 
                type="submit" 
                className="project-form__btn project-form__btn--confirm" 
                disabled={!name.trim()}
              >
                {isEditing ? t('common.save') : t('common.create')}
              </button>
            </div>
          )}
        </div>
      </form>
    </Modal>
  );
}

export default ProjectForm;
